import React, { useMemo, useState } from 'react';
import { CalculationResult, activeRepsForRow } from '../utils/roiCalculations';
import { fmtMoney, fmtPct } from '../utils/formatters';
interface ResultsSummaryProps {
  results: CalculationResult;
  halfSales?: boolean;
  doubleAttrition?: boolean;
  timeframe?: 3 | 6 | 12;
  onOptimizeForTarget?: (target: number, months: 3 | 6 | 12) => void;
  onOptimizeForTeamTarget?: (target: number, months: 3 | 6 | 12) => void;
  onUndoOptimize?: () => void;
  canUndoOptimize?: boolean;
}
export function ResultsSummary({
  results,
  halfSales = false,
  doubleAttrition = false,
  timeframe = 12,
  onOptimizeForTarget,
  onOptimizeForTeamTarget,
  onUndoOptimize,
  canUndoOptimize = false
}: ResultsSummaryProps) {
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetValue, setTargetValue] = useState('');
  const [editingTeamTarget, setEditingTeamTarget] = useState(false);
  const [teamTargetValue, setTeamTargetValue] = useState('');
  const calculatedTotals = useMemo(() => {
    const lastRow = results.rows[timeframe];
    if (!lastRow) {
      return {
        totalGrossRevenue: 0,
        totalGrossProfit: 0,
        totalNetProfit: 0,
        roi: 0
      };
    }
    let cumGrossRevenue = 0;
    let cumGrossProfit = 0;
    let cumTotalCost = 0;
    for (let i = 0; i <= timeframe; i++) {
      cumGrossRevenue += results.rows[i].grossRevenue;
      cumGrossProfit += results.rows[i].grossProfit;
      cumTotalCost += results.rows[i].totalCost;
    }
    if (halfSales) {
      cumGrossRevenue = cumGrossRevenue * 0.5;
      cumGrossProfit = cumGrossProfit * 0.5;
    }
    if (doubleAttrition) {
      cumGrossRevenue = cumGrossRevenue * 0.5;
      cumGrossProfit = cumGrossProfit * 0.5;
    }
    const roi = cumTotalCost > 0 ? cumGrossProfit / cumTotalCost * 100 : 0;
    const totalNetProfit = cumGrossProfit - cumTotalCost;
    return {
      totalGrossRevenue: cumGrossRevenue,
      totalGrossProfit: cumGrossProfit,
      totalNetProfit,
      roi
    };
  }, [results.rows, halfSales, doubleAttrition, timeframe]);
  // Net team growth mirrors the projection table's Active Reps (Average) metric
  // for the final month shown: launched (0-30d at 50%) + 30d+ + 60d+ + 90d+.
  const netTeamGrowth = useMemo(() => {
    const lastRow = results.rows[timeframe];
    if (!lastRow) return 0;
    return activeRepsForRow(lastRow);
  }, [results.rows, timeframe]);
  const canOptimizeTeam = !!onOptimizeForTeamTarget;
  const startEditingTeamTarget = () => {
    setTeamTargetValue(netTeamGrowth.toFixed(1));
    setEditingTeamTarget(true);
  };
  const commitTeamTarget = () => {
    const parsed = parseFloat(teamTargetValue);
    setEditingTeamTarget(false);
    if (!isNaN(parsed) && parsed > 0 && onOptimizeForTeamTarget) {
      onOptimizeForTeamTarget(parsed, timeframe);
    }
  };
  const handleTeamTargetKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      commitTeamTarget();
    } else if (e.key === 'Escape') {
      setEditingTeamTarget(false);
    }
  };
  const roiClass = calculatedTotals.roi >= 0 ? 'text-green-600' : 'text-red-600';
  const startEditingTarget = () => {
    setTargetValue(String(Math.round(calculatedTotals.totalGrossRevenue)));
    setEditingTarget(true);
  };
  const commitTarget = () => {
    const parsed = parseFloat(targetValue);
    setEditingTarget(false);
    if (!isNaN(parsed) && parsed > 0 && onOptimizeForTarget) {
      onOptimizeForTarget(parsed, timeframe);
    }
  };
  const handleTargetKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      commitTarget();
    } else if (e.key === 'Escape') {
      setEditingTarget(false);
    }
  };
  const canOptimize = !!onOptimizeForTarget;
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6" data-id="element-525" data-mp="2pffcn1j8gvjl">
      {/* Net Team Growth — active reps in the final month shown in projections */}
      <div className="p-4 md:p-6 rounded-xl bg-gray-50 border border-gray-200" data-id="element-526" data-mp="2fi14dp7grd2d">
        <div className="text-sm text-gray-600 mb-2 font-semibold" data-id="element-527" data-mp="2fjvhihchu0vw">
          {timeframe}-Month Net Team Growth
        </div>
        {editingTeamTarget ? <div className="relative" data-id="element-528" data-mp="23gsxg00405b5">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-lg" data-id="element-529" data-mp="23huf4nbmrq1u">
              +
            </span>
            <input type="number" min="0" step="1" value={teamTargetValue} onChange={e => setTeamTargetValue(e.target.value)} onKeyDown={handleTeamTargetKeyDown} onBlur={commitTeamTarget} autoFocus className="w-full pl-6 pr-2 py-1 text-2xl md:text-3xl font-bold text-navy border-2 border-amber rounded outline-none bg-white" data-id="element-530" data-mp="1tqq4fzszmrct" />
          </div> : <div onClick={canOptimizeTeam ? startEditingTeamTarget : undefined} className={`text-2xl md:text-3xl font-bold text-navy ${canOptimizeTeam ? 'cursor-pointer hover:text-amber transition-colors underline decoration-dotted decoration-gray-400 underline-offset-4' : ''}`} title={canOptimizeTeam ? 'Click to set a team-growth target — campaigns will auto-optimize' : undefined} data-id="element-531" data-mp="3a4pxqrxbvir3">
            +{netTeamGrowth.toFixed(1)}
          </div>}
        <div className="text-sm text-gray-600 mt-2 flex items-center gap-2" data-id="element-532" data-mp="3rfmtw8rg4aba">
          {canOptimizeTeam ? <span data-id="element-533" data-mp="2upmayqdrco1w">Click to set a Month {timeframe} rep target</span> : <span data-id="element-534" data-mp="2yqxs07ikg0yh">Active reps at Month {timeframe}</span>}
          {canUndoOptimize && onUndoOptimize && <button type="button" onClick={onUndoOptimize} className="text-amber-600 hover:text-amber font-semibold underline underline-offset-2" data-id="element-535" data-mp="38n1342veb9xk">
              Undo
            </button>}
        </div>
      </div>

      {/* Gross Revenue - click to set a target and auto-optimize campaigns */}
      <div className="p-4 md:p-6 rounded-xl bg-gray-50 border border-gray-200" data-id="element-536" data-mp="3uchb6m3gxn37">
        <div className="text-sm text-gray-600 mb-2 font-semibold" data-id="element-537" data-mp="3pdxq0nwp8fbw">
          {timeframe}-Month Gross Revenue
          {(halfSales || doubleAttrition) && <span className="ml-2 text-xs text-orange-600 font-bold" data-id="element-538" data-mp="37rawxbd91i6">
              (Scenario Active)
            </span>}
        </div>
        {editingTarget ? <div className="relative" data-id="element-539" data-mp="3bnubyk7z3rxj">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-lg" data-id="element-540" data-mp="36opxbe2a51zt">
              $
            </span>
            <input type="number" min="0" step="10000" value={targetValue} onChange={e => setTargetValue(e.target.value)} onKeyDown={handleTargetKeyDown} onBlur={commitTarget} autoFocus className="w-full pl-6 pr-2 py-1 text-2xl md:text-3xl font-bold text-navy border-2 border-amber rounded outline-none bg-white" data-id="element-541" data-mp="3lex9xd5lvqsl" />
          </div> : <div onClick={canOptimize ? startEditingTarget : undefined} className={`text-2xl md:text-3xl font-bold text-navy ${canOptimize ? 'cursor-pointer hover:text-amber transition-colors underline decoration-dotted decoration-gray-400 underline-offset-4' : ''}`} title={canOptimize ? 'Click to set a target — campaigns will auto-optimize' : undefined} data-id="element-542" data-mp="187pima9m5kbv">
            {fmtMoney(calculatedTotals.totalGrossRevenue)}
          </div>}
        <div className="text-sm text-gray-600 mt-2 flex items-center gap-2" data-id="element-543" data-mp="1d83gx0lexfwp">
          {canOptimize ? <span data-id="element-544" data-mp="1qllqphwdols">Click to set a {timeframe}-mo target</span> : <span data-id="element-545" data-mp="1somo4kl925a">Cumulative top-line revenue</span>}
          {canUndoOptimize && onUndoOptimize && <button type="button" onClick={onUndoOptimize} className="text-amber-600 hover:text-amber font-semibold underline underline-offset-2" data-id="element-546" data-mp="5puhu10etyki">
              Undo
            </button>}
        </div>
      </div>

      {/* Gross Profit */}
      <div className="p-4 md:p-6 rounded-xl bg-gray-50 border border-gray-200" data-id="element-547" data-mp="jfz4xsq4mlg6">
        <div className="text-sm text-gray-600 mb-2 font-semibold" data-id="element-548" data-mp="ji25v7sthyzo">
          {timeframe}-Month Gross Profit
          {(halfSales || doubleAttrition) && <span className="ml-2 text-xs text-orange-600 font-bold" data-id="element-549" data-mp="1c5pk853hvtdv">
              (Scenario Active)
            </span>}
        </div>
        <div className="text-2xl md:text-3xl font-bold text-green-600" data-id="element-550" data-mp="17btsvlhql4ro">
          {fmtMoney(calculatedTotals.totalGrossProfit)}
        </div>
        <div className="text-sm text-gray-600 mt-2" data-id="element-551" data-mp="1zuh5sdmkxdho">
          GP / Sale: {fmtMoney(results.gpPerSale)}
        </div>
      </div>

      {/* ROI */}
      <div className="p-4 md:p-6 rounded-xl bg-gray-50 border border-gray-200" data-id="element-552" data-mp="1v1fojyywh1ux">
        <div className="text-sm text-gray-600 mb-2 font-semibold" data-id="element-553" data-mp="22g84aaxicmw3">
          {timeframe}-Month ROI
          {(halfSales || doubleAttrition) && <span className="ml-2 text-xs text-orange-600 font-bold" data-id="element-554" data-mp="2cd32espvcl9u">
              (Scenario Active)
            </span>}
        </div>
        <div className={`text-2xl md:text-3xl font-bold ${roiClass}`} data-id="element-555" data-mp="2gf7wtto55lxj">
          {Math.round(calculatedTotals.roi)}%
        </div>
        <div className="text-sm text-gray-600 mt-2" data-id="element-556" data-mp="2lbosra1l14l6">
          Net Profit: {fmtMoney(calculatedTotals.totalNetProfit)}
        </div>
      </div>
    </div>;
}